import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../../contexts/UserContext";
import { fetchTransactions } from "../../../domain/services/transactions/fetchTransactions";
import { useFamilyBankAccounts } from "../../../domain/services/hooks/useFamilyBankAccounts";

/**
 * Custom hook to fetch and manage family transactions.
 *
 * This hook leverages React Query to fetch transactions for all family members' accounts.
 * It handles user authentication, data fetching, and caching of transaction data.
 *
 * @param transactionType - Type of transactions to fetch ('all', 'income', 'expense').
 *                          Defaults to 'all' if not specified.
 * @param limit - Maximum number of transactions to fetch. Defaults to 100.
 * @returns An object containing the data, loading state, and any error encountered.
 *
 * @example
 * const { data, isLoading, error } = useFamilyTransactions('income', 50);
 */
export const useFamilyTransactions = (transactionType = "all", limit = 100) => {
  const { currentUser } = useUser();
  const { familyBankAccounts, loading: familyAccountsLoading } =
    useFamilyBankAccounts();

  return useQuery({
    queryKey: ["familyTransactions", currentUser?.uid, transactionType],
    queryFn: async () => {
      const accessToken = await currentUser?.getIdToken();
      console.debug("Family Bank Accounts:", familyBankAccounts);

      if (!accessToken) {
        throw new Error("No access token available");
      }

      if (!familyBankAccounts || Object.keys(familyBankAccounts).length === 0) {
        console.debug("No family bank accounts found");
        return {
          transactions: [],
          total_transactions: 0,
          next_cursor: null,
        };
      }

      const accountIds = Object.fromEntries(
        Object.entries(familyBankAccounts).map(([appUserId, accounts]) => [
          appUserId,
          accounts.map((account) => account.accountId),
        ]),
      );
      console.debug("Transformed accountIds:", accountIds);

      const familyTransactions = await fetchTransactions(
        accessToken,
        accountIds,
        transactionType,
        {
          limit,
          cursor: null,
          sortBy: "date",
          sortOrder: "desc",
        },
      );

      console.debug("Fetched family transactions:", familyTransactions);
      return familyTransactions;
    },
    enabled: !!currentUser && !!familyBankAccounts && !familyAccountsLoading,
    staleTime: 5 * 60 * 1000, // Add caching for 5 minutes
    retry: 2,
  });
};
